import React, { useState, useEffect, useMemo } from 'react';
import { useCameraData } from '../hooks/useCameraData';

const LazyEmotionTooltip = ({ artworkId, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  
  // Only fetch data when shouldFetch is true (after hover)
  const { rawData, loading, error } = useCameraData(
    shouldFetch ? artworkId : null, 
    'emotion'
  );

  // Don't show loading state until we actually start fetching
  const isLoading = shouldFetch && loading;

  // Start fetching when hovered
  useEffect(() => {
    if (isHovered && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [isHovered, shouldFetch]);

  // Calculate emotion statistics
  const emotionStats = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return null;
    }

    const emotionCounts = {
      positive: 0,
      neutral: 0,
      negative: 0
    };

    let totalDuration = 0;
    let sessionCount = 0;

    rawData.forEach((session) => {
      const emotion = (session.Emotion || session.emotion || '').toLowerCase();
      const duration = session.Session_Duration || session.session_duration || 0;
      
      if (emotionCounts.hasOwnProperty(emotion)) {
        emotionCounts[emotion] += 1;
      }
      
      totalDuration += duration;
      sessionCount += 1;
    });

    const totalSessions = emotionCounts.positive + emotionCounts.neutral + emotionCounts.negative;
    const avgDuration = sessionCount > 0 ? (totalDuration / sessionCount).toFixed(1) : 0;
    
    // Calculate percentages
    const percentages = {
      positive: totalSessions > 0 ? ((emotionCounts.positive / totalSessions) * 100).toFixed(1) : 0,
      neutral: totalSessions > 0 ? ((emotionCounts.neutral / totalSessions) * 100).toFixed(1) : 0,
      negative: totalSessions > 0 ? ((emotionCounts.negative / totalSessions) * 100).toFixed(1) : 0
    };

    return {
      counts: emotionCounts,
      totalSessions,
      avgDuration,
      percentages
    };
  }, [rawData]);

  const emotionColors = {
    positive: '#10b981', // emerald-500
    neutral: '#6b7280',   // gray-500
    negative: '#ef4444'    // red-500
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && (
        <div 
          className="absolute z-50 w-64 p-4 bg-gray-900 text-white rounded-lg shadow-2xl border border-gray-700 pointer-events-none"
          style={{
            bottom: '50%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            maxWidth: 'calc(100vw - 2rem)'
          }}
        >
          {!shouldFetch ? (
            <div className="text-sm text-gray-300">Loading emotion data...</div>
          ) : isLoading ? (
            <div className="text-sm text-gray-300">Loading emotion data...</div>
          ) : error ? (
            <div className="text-sm text-red-400">Error loading data</div>
          ) : !emotionStats || emotionStats.totalSessions === 0 ? (
            <div className="text-sm text-gray-300">No emotion data available</div>
          ) : (
            <div className="space-y-3">
              <div className="border-b border-gray-700 pb-2">
                <h4 className="font-bold text-sm text-gray-200 mb-1">Emotion Analytics</h4>
                <div className="text-xs text-gray-400">
                  {emotionStats.totalSessions} total sessions
                </div>
                <div className="text-xs text-gray-400">
                  Avg duration: {emotionStats.avgDuration}s
                </div>
              </div>
              
              <div className="space-y-2">
                {['positive', 'neutral', 'negative'].map((emotion) => {
                  const count = emotionStats.counts[emotion];
                  const percentage = emotionStats.percentages[emotion];
                  const color = emotionColors[emotion];
                  
                  return (
                    <div key={emotion} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs capitalize text-gray-300">{emotion}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold" style={{ color }}>
                          {count}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div
            className="absolute w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-900"
            style={{
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default LazyEmotionTooltip;

